import { Link } from 'react-router-dom';
import { MARGIN_RELATED_TOOLS } from '@/mocks/margin';
import { SectionHeading } from './ui';

type Mistake = {
  number: string;
  badge: string;
  badgeClass: string;
  title: string;
  desc: string;
  advice: string;
  highlight: string;
};

const MISTAKES: Mistake[] = [
  {
    number: '01',
    badge: 'LỖI ĐÒN BẨY',
    badgeClass: 'bg-red-50 text-red-600',
    title: 'Chọn đòn bẩy quá cao so với chiến lược',
    desc: 'Đòn bẩy cao làm ký quỹ yêu cầu giảm xuống, dễ khiến bạn ảo tưởng và mở vị thế quá lớn so với số dư thực tế. Chỉ một biến động ngược hướng nhỏ cũng đủ kích hoạt lệnh gọi ký quỹ (Margin Call).',
    advice: 'Khuyên dùng: Giữ đòn bẩy ổn định',
    highlight: 'Tối đa 1:100 hoặc 1:200',
  },
  {
    number: '02',
    badge: 'LỖI QUY ĐỔI',
    badgeClass: 'bg-orange-50 text-orange-600',
    title: 'Bỏ qua quy mô và kích thước hợp đồng',
    desc: 'Giao dịch Vàng (100 oz/lot) khác hẳn với ngoại hối thông thường (100,000 đơn vị/lot). Tính sai đơn vị hợp đồng sẽ dẫn đến sai lệch hoàn toàn về số tiền ký quỹ bị phong tỏa.',
    advice: 'Khuyên dùng: Kiểm tra quy cách tài sản',
    highlight: 'Xác thực kích thước Lot',
  },
  {
    number: '03',
    badge: 'LỖI TIỀN TỆ',
    badgeClass: 'bg-blue-50 text-blue-600',
    title: 'Nhầm lẫn đồng tiền định giá tài khoản',
    desc: 'Ký quỹ luôn được tính bằng đồng tiền định giá của tài sản. Bạn cần quy đổi chính xác về đồng tiền của tài khoản (USD, EUR, VND) để biết số tiền thực tế bị giữ là bao nhiêu.',
    advice: 'Khuyên dùng: Đồng bộ đơn vị tiền tệ',
    highlight: 'Quy đổi tỷ giá thực tế',
  },
  {
    number: '04',
    badge: 'LỖI THỜI GIAN THỰC',
    badgeClass: 'bg-purple-50 text-purple-600',
    title: 'Không tính toán lại theo giá thị trường hiện tại',
    desc: 'Giá thị trường luôn biến động không ngừng, làm thay đổi giá trị danh nghĩa của hợp đồng. Cùng một khối lượng lệnh, giá càng cao thì ký quỹ yêu cầu thực tế sẽ càng lớn.',
    advice: 'Khuyên dùng: Cập nhật giá liên tục',
    highlight: 'Sử dụng dữ liệu real-time',
  },
];

export function MistakesSection() {
  return (
    <section>
      <div className="text-center">
        <span className="text-xs font-bold uppercase tracking-widest text-purple-600">
          RỦI RO &amp; SAI LẦM
        </span>
        <h2 className="mx-auto mt-3 max-w-2xl text-3xl font-extrabold text-slate-900 lg:text-4xl">
          Những sai lầm thường gặp khi tính ký quỹ
        </h2>
        <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-slate-500">
          Tránh những lỗi phổ biến để kiểm soát đòn bẩy và bảo vệ tài khoản hiệu quả hơn.
        </p>
      </div>

      <div className="mt-16 grid grid-cols-1 gap-8 md:grid-cols-2">
        {MISTAKES.map((m) => (
          <div
            key={m.number}
            className="flex flex-col justify-between rounded-3xl border border-slate-200/80 bg-white p-8 shadow-sm transition-all hover:border-purple-300 lg:p-10"
          >
            <div>
              <div className="mb-6 flex items-center justify-between">
                <span className="text-2xl font-black text-purple-600">{m.number}</span>
                <span className={`rounded-full px-3 py-1 text-[11px] font-bold ${m.badgeClass}`}>
                  {m.badge}
                </span>
              </div>
              <h3 className="mb-3 text-2xl font-extrabold leading-snug text-slate-900">{m.title}</h3>
              <p className="text-sm leading-relaxed text-slate-600">{m.desc}</p>
            </div>
            <div className="mt-6 flex items-center justify-between border-t border-slate-100 pt-4 text-xs">
              <span className="text-slate-400">{m.advice}</span>
              <span className="whitespace-nowrap font-bold text-purple-600">{m.highlight}</span>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

export function RelatedToolsSection() {
  return (
    <section>
      <SectionHeading
        eyebrow="Công cụ"
        title="Công cụ liên quan"
        subtitle="Khám phá thêm các công cụ tính toán hỗ trợ giao dịch của bạn."
      />

      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {MARGIN_RELATED_TOOLS.map((t) => (
          <Link
            key={t.title}
            to={t.to}
            className="group rounded-2xl border border-slate-200/80 bg-white p-5 transition-all hover:-translate-y-0.5 hover:border-violet-200"
          >
            <span
              className="flex h-10 w-10 items-center justify-center rounded-xl text-white transition-transform group-hover:scale-105"
              style={{ backgroundColor: t.accent }}
            >
              <i className={`${t.icon} text-lg`} />
            </span>
            <p className="mt-4 text-sm font-semibold text-slate-800">{t.title}</p>
            <p className="mt-1.5 text-xs leading-relaxed text-slate-400">{t.desc}</p>
            <span className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-violet-600">
              Mở công cụ
              <i className="ri-arrow-right-line transition-transform group-hover:translate-x-0.5" />
            </span>
          </Link>
        ))}
      </div>
    </section>
  );
}